"use client";

import type { Alignment } from "@/app/_api/generateTts";
import type { Voice } from "@/app/_data/voices";
import type { LanguageOrAccent } from "@components/languages/config";
import type { IPrompt, IUseCasePromptWithId } from "@components/languages/types";

import { captureEvent } from "@/app/_analytics/analytics";
import { EventName } from "@mw/shared/analyticsEventNames";
import { errorMap, generateDialogueTts, generateStreamingTts, generateTts } from "@/app/_api/generateTts";
import { getCaptchaToken } from "@/app/_providers/hcaptcha/core";
import { toast } from "@/app/_providers/toast";
import MediaProgressBar from "@components/core/MediaProgressBar";
import { ttsLanguages as languages, ttsLanguagesMap } from "@components/languages/config";
import { defaultV3Prompt } from "@components/languages/promptData/en";
import { PromptFeature } from "@components/languages/types";
import { TTSModels } from "@mw/shared/models";
import { getDefaultPrompt } from "@ui/components/languages/textPrompts";
import { cn } from "@ui/lib/utils";
import { runAfterNextBrowserPaint } from "@utilities/afterNextPaint";
import { logger } from "@utilities/logger";
import { ExpandIcon, LoaderCircleIcon, Settings2Icon, ShrinkIcon } from "lucide-react";
import * as m from "motion/react-m";
import { useLocale, useTranslations } from "next-intl";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { MultiSpeakerInputRef, MultiSpeakerSample } from "./MultiSpeakerInput";
import type { SamplePrompt } from "./config";
import type { AnimationState, PlayingState } from "./constants";

import { Button } from "../../core/Button";
import { LimitReachedOverlay } from "../LimitReachedOverlay";
import { OmniboxComponentWrapper } from "../OmniboxComponentWrapper";
import { MultiSpeakerInput } from "./MultiSpeakerInput";
import { SimpleMobile } from "./SimpleMobile";
import {
  DownloadButton,
  LanguageSelect,
  ModelSelector,
  PlayButton,
  SpeedSelector,
  VoiceSelector,
} from "./components/TTSControls";
import { TextAreaWithHighlighter } from "./components/TTSTextInput";
import { VoicePickerTags } from "./components/VoicePickerTags";
import { CHARACTER_LIMIT } from "./constants";
import { getVoices } from "./fetchVoices";
import { getAvailableTTSModels, getDefaultTTSModel } from "./ttsUtilts";
import { convertDialogueToSamples } from "./ttsUtilts";
import { clsx } from "clsx";
import { OmniboxSimpleWrapper } from "../OmniboxSimpleWrapper";

function sortLanguagesWithEnglishFirst(a: LanguageOrAccent, b: LanguageOrAccent) {
  // always put english first
  if (a.value === "en") return -1;
  if (b.value === "en") return 1;
  return a.label.localeCompare(b.label);
}

export interface TTSProps {
  cardsLayout?: boolean;
  customTtsGenerator?: (props: {
    abortController?: AbortController;
    audioElement: HTMLAudioElement;
    config: { text: string };
    hcaptcha_token?: null | string;
    modelId: string;
    voiceId: string;
  }) => Promise<void>;
  defaultLanguage?: null | string;
  forceEditor?: boolean;
  hideSamples?: boolean;
  initialPrompt?: Omit<IPrompt, "icon"> | false;
  initialPrompts?: IUseCasePromptWithId[];
  model: string;
  placeholder?: string;
  samplePrompts?: SamplePrompt[] | null;
  size?: "md" | "xl";
  useStreaming?: boolean;
  variant?: "default" | "sidebar" | "simple";
  voices?: Voice[] | null;
}

// Currently just re-exports TTS, but this is a good entry point to introduce feature flags and loading skeletons for experiments
export default function TTSWithClientFlag(props: TTSProps) {
  return <TTS {...props} />;
}

function TextInput({
  isDialogMode,
  selectedDialogue,
  voices,
  markNeedsRegeneration,
  multiSpeakerRef,
  alignment,
  audioElementRef,
  useStreaming,
  handleTextChange,
  playingState,
  text,
  model,
  placeholder,
}: {
  alignment: Alignment | null;
  audioElementRef: React.RefObject<HTMLAudioElement | null>;
  handleTextChange: (text: string) => void;
  isDialogMode: boolean;
  markNeedsRegeneration: () => void;
  model: TTSModels;
  multiSpeakerRef: React.RefObject<MultiSpeakerInputRef | null>;
  placeholder: string | undefined;
  playingState: PlayingState;
  selectedDialogue: { assignedVoices: Voice[]; samples: MultiSpeakerSample[] } | null;
  text: string;
  useStreaming: boolean;
  voices: Voice[];
}) {
  return isDialogMode ? (
    <MultiSpeakerInput
      availableVoices={selectedDialogue?.assignedVoices || voices}
      initialSamples={selectedDialogue?.samples || []}
      onChange={markNeedsRegeneration}
      onDirty={markNeedsRegeneration}
      ref={multiSpeakerRef}
    />
  ) : (
    <TextAreaWithHighlighter
      alignment={alignment}
      audioElementRef={audioElementRef}
      isStreaming={useStreaming ?? false}
      onTextChange={handleTextChange}
      placeholder={placeholder}
      playingState={playingState}
      text={text}
      withBrackets={model === TTSModels.ELEVEN_V3}
    />
  );
}

function Wrapper({
  variant,
  children,
  size,
}: {
  variant: TTSProps["variant"];
  children: React.ReactNode;
  size?: TTSProps["size"];
}) {
  if (variant === "simple") {
    return <div>{children}</div>;
  }
  return (
    <OmniboxComponentWrapper component='tts' initiallyVisible={true} size={size || "2xl"}>
      {children}
    </OmniboxComponentWrapper>
  );
}

export function TTS({
  voices: overrideVoices,
  model: modelOverride,
  size = "md",
  customTtsGenerator,
  defaultLanguage: initialLanguage,
  samplePrompts,
  initialPrompt,
  initialPrompts,
  useStreaming = true,
  hideSamples,
  forceEditor = true,
  placeholder,
  variant = "default",
}: Omit<TTSProps, "forceV3Enabled">) {
  const audioElementRef = useRef<HTMLAudioElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const multiSpeakerRef = useRef<MultiSpeakerInputRef>(null);
  const generateAbortControllerRef = useRef<AbortController | null>(null);
  const elementRect = useRef<{ height: number; left: number; top: number; width: number } | null>(null);

  const t = useTranslations("ClientKeys.TTS");
  const locale = useLocale();

  // State
  const [selectedLanguage, setSelectedLanguage] = useState(initialLanguage ?? locale);
  const [model, setModel] = useState<TTSModels>(() =>
    modelOverride ? (modelOverride as TTSModels) : getDefaultTTSModel(initialLanguage ?? locale),
  );
  const [text, setText] = useState(() => {
    if (initialPrompt && initialPrompt.text) {
      return initialPrompt.text;
    }
    if (initialPrompt === false) return "";
    return defaultV3Prompt.text;
  });
  const [speed, setSpeed] = useState(1);
  const [regenerate, setRegenerate] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const [animationState, setAnimationState] = useState<AnimationState>("original");
  const [showLimitReachedOverlay, setShowLimitReachedOverlay] = useState(false);
  const [alignment, setAlignment] = useState<Alignment | null>(null);
  const [playingState, setPlayingState] = useState<PlayingState>("idle");
  const [selectedDialogue, setSelectedDialogue] = useState<{
    assignedVoices: Voice[];
    samples: MultiSpeakerSample[];
  } | null>(null);
  const [completeAudioData, setCompleteAudioData] = useState<Uint8Array | null>(null);
  const [voices, setVoices] = useState<Voice[]>(overrideVoices ?? []);
  const [selectedVoice, setSelectedVoice] = useState<Voice | undefined>(undefined);
  const [voicesLoading, setVoicesLoading] = useState(false);
  const [hideSimpleView, setHideSimpleView] = useState(!initialPrompts?.length || forceEditor);

  const [activeSampleId, setActiveSampleId] = useState<null | string>(null);

  // Derived state
  const language = ttsLanguagesMap[selectedLanguage];

  const isDialogMode = selectedDialogue !== null;
  const availableModels = getAvailableTTSModels(selectedLanguage);

  const sortedLanguages = useMemo(
    () =>
      // English should always be first
      model === TTSModels.ELEVEN_V3
        ? languages.sort(sortLanguagesWithEnglishFirst)
        : languages
            .filter(lang => ttsLanguagesMap[lang.value]?.tts?.[TTSModels.ELEVEN_MULTILINGUAL_V2])
            .sort(sortLanguagesWithEnglishFirst),
    [model],
  );

  // Update selected voice when voices change
  useEffect(() => {
    if (voices.length > 0 && !selectedVoice) {
      const preferredVoice = defaultV3Prompt.preferredVoices?.[0];
      if (preferredVoice && voices.find(voice => voice.voiceId === preferredVoice.voiceId)) {
        setSelectedVoice(preferredVoice);
      } else {
        setSelectedVoice(voices[0]);
      }
    }
  }, [voices, selectedVoice]);

  const updateVoices = useCallback(
    (newVoices: Voice[]) => {
      setVoices(newVoices);
      // Update selected voice if current one is not in the new list
      if (newVoices.length > 0 && !newVoices.find(voice => voice.voiceId === selectedVoice?.voiceId)) {
        setSelectedVoice(newVoices[0]);
      }
    },
    [selectedVoice],
  );

  // Fetch voices for the current language
  const fetchVoicesForCurrentLanguage = useCallback(
    async (languageCode: string) => {
      // If overrideVoices are provided, use them instead of fetched voices
      if (overrideVoices?.length && selectedLanguage === initialLanguage) {
        updateVoices(overrideVoices);
        return;
      }

      setVoicesLoading(true);
      const voices = await getVoices(languageCode);
      updateVoices(voices);
      setVoicesLoading(false);
    },
    [initialLanguage, overrideVoices, selectedLanguage, updateVoices],
  );

  // Animation state management - keep this useEffect as it handles complex timing
  useEffect(() => {
    if (isExpanded && containerRef.current) {
      document.body.style.overflow = "hidden";
      const rect = containerRef.current.getBoundingClientRect();
      elementRect.current = { height: rect.height, left: rect.left, top: rect.top, width: rect.width };
      setAnimationState("popping");
      setTimeout(() => setAnimationState("full-screen"), 50);
    } else if (!isExpanded && elementRect.current) {
      setAnimationState("popping-back");
      setTimeout(() => {
        setAnimationState("original");
        elementRect.current = null;
        document.body.style.overflow = "auto";
      }, 300);
    }
  }, [isExpanded]);

  // Actions - explicit functions instead of useEffect chains
  const updateDefaultPrompt = useCallback((language: string, model: TTSModels) => {
    getDefaultPrompt(language, model)
      .then(prompt => {
        setText(prompt?.text || "");
      })
      .catch(error => {
        logger.error(error instanceof Error ? error : null, "Error getting default prompt");
      });
  }, []);

  const handleLanguageChange = useCallback(
    async (newLanguage: string) => {
      audioElementRef.current?.pause();

      captureEvent(EventName.mw_tts_language_changed, {
        previous_language: selectedLanguage,
        new_language: newLanguage,
      });

      // Fetch voices for new language immediately
      await fetchVoicesForCurrentLanguage(newLanguage);
      setSelectedLanguage(newLanguage);

      // Update text based on active model
      updateDefaultPrompt(newLanguage, model);
      setRegenerate(true);
    },
    [fetchVoicesForCurrentLanguage, model, updateDefaultPrompt, selectedLanguage],
  );

  const handleModelChange = useCallback(
    (newModel: TTSModels) => {
      captureEvent(EventName.mw_tts_model_changed, {
        current_language: selectedLanguage,
        previous_model_id: model,
        new_model_id: newModel,
      });

      setModel(newModel);

      updateDefaultPrompt(selectedLanguage, newModel);
      setRegenerate(true);
    },
    [selectedLanguage, updateDefaultPrompt, model],
  );

  const handleVoiceChange = useCallback(
    (newVoice: Voice) => {
      captureEvent(EventName.mw_tts_voice_changed, {
        previous_voice_id: selectedVoice?.voiceId,
        new_voice_id: newVoice.voiceId,
      });

      setSelectedVoice(newVoice);
      setRegenerate(true);
    },
    [selectedVoice],
  );

  const handleTextChange = useCallback((newText: string) => {
    requestAnimationFrame(() => {
      setText(newText);
      setRegenerate(true);
    });
  }, []);

  const handleSpeedChange = useCallback((newSpeed: number) => {
    setSpeed(newSpeed);
    setRegenerate(true);
  }, []);

  const markNeedsRegeneration = useCallback(() => {
    setRegenerate(true);
  }, []);

  const generate = useCallback(
    async ({
      textPrompt,
      voice,
      isDialogueMode = false,
      dialogueModeSamples,
      modelId,
    }: {
      dialogueModeSamples?: MultiSpeakerSample[];
      isDialogueMode?: boolean;
      modelId: string;
      textPrompt?: string;
      voice?: Voice;
    }) => {
      if (!audioElementRef.current) return;

      setPlayingState("loading");
      setCompleteAudioData(null);
      // abort any previous generation
      generateAbortControllerRef.current?.abort();
      const abortController = new AbortController();
      generateAbortControllerRef.current = abortController;

      setAlignment(null);
      setRegenerate(false);

      let cleanupFunction: (() => void) | undefined = undefined;
      let eventProperties: Record<string, unknown> = {};
      const startedEvent = isDialogueMode
        ? EventName.mw_tts_dialogue_mode_generation_started
        : EventName.landing_page_tts_audio_generated;
      const completedEvent = isDialogueMode
        ? EventName.mw_tts_dialogue_mode_generation_completed
        : EventName.mw_tts_generation_completed;
      if (isDialogueMode) {
        eventProperties = {
          source_component: "tts_omnibox",
          tts_mode: "dialogue",
          model_id: modelId,
          language: selectedLanguage,
          text_length: dialogueModeSamples?.reduce((acc, sample) => acc + sample.text.length, 0),
          speed,
        };
      } else {
        eventProperties = {
          source_component: "tts_omnibox",
          tts_mode: "normal",
          voice_id: voice?.voiceId,
          model_id: modelId,
          language: selectedLanguage,
          text: textPrompt || text,
          text_length: textPrompt?.length || text.length,
          speed,
        };
      }

      try {
        captureEvent(startedEvent, eventProperties);
        captureEvent(EventName.mw_tts_voice_heard, {
          ...eventProperties,
          audio_type: "generation",
        });
        const token = await getCaptchaToken({ behaviourType: "tts" });

        if (customTtsGenerator) {
          await customTtsGenerator({
            voiceId: voice?.voiceId || selectedVoice?.voiceId || "",
            config: { text: textPrompt || text },
            modelId,
            audioElement: audioElementRef.current,
            hcaptcha_token: token,
          });
          captureEvent(completedEvent, eventProperties);
          setPlayingState("idle");
        } else {
          if (isDialogueMode) {
            const samples = (dialogueModeSamples || multiSpeakerRef.current?.getSamples() || []).map(sample => ({
              text: sample.text,
              voice_id: sample.voiceId,
            }));
            const response = generateDialogueTts({
              audioElement: audioElementRef.current,
              abortController,
              config: { inputs: samples, language_code: selectedLanguage, hcaptcha_token: token },
            });
            await response.promise;
            cleanupFunction = response.cleanup;
            captureEvent(completedEvent, eventProperties);
          } else {
            if (useStreaming) {
              const { cleanup, promise } = generateStreamingTts({
                voiceId: voice?.voiceId || selectedVoice?.voiceId || "",
                config: { text: textPrompt || text, language_code: selectedLanguage },
                modelId,
                audioElement: audioElementRef.current,
                speed,
                hcaptcha_token: token,
                onTimestampsReceived: response => {
                  if (response.alignment) {
                    // Update alignment state with new chunks
                    setAlignment(existingAlignment => {
                      if (!existingAlignment) {
                        return response.alignment || null;
                      }
                      if (!response.alignment) {
                        return existingAlignment;
                      }
                      return {
                        character_end_times_seconds: [
                          ...existingAlignment.character_end_times_seconds,
                          ...response.alignment.character_end_times_seconds,
                        ],
                        character_start_times_seconds: [
                          ...existingAlignment.character_start_times_seconds,
                          ...response.alignment.character_start_times_seconds,
                        ],
                        characters: [...existingAlignment.characters, ...response.alignment.characters],
                      };
                    });
                  }
                },
                onStreamComplete: audioData => {
                  setCompleteAudioData(audioData);
                },
                abortController,
              });
              await promise;
              cleanupFunction = cleanup;
              captureEvent(completedEvent, eventProperties);
            } else {
              const response = await generateTts({
                voiceId: voice?.voiceId || selectedVoice?.voiceId || "",
                config: { text: textPrompt || text, language_code: selectedLanguage },
                modelId,
                audioElement: audioElementRef.current,
                speed,
                hcaptcha_token: token,
                withTimestamps: true,
                abortController,
              });
              if (abortController.signal.aborted) return;
              setAlignment(response?.alignment ?? null);
              captureEvent(completedEvent, eventProperties);
              audioElementRef.current?.play();
            }
          }
        }
      } catch (e: unknown) {
        if (e instanceof DOMException && e.name === "AbortError") return;

        const errorEvent = isDialogueMode
          ? EventName.mw_tts_dialogue_mode_generation_error
          : EventName.mw_tts_generation_error;
        if (typeof e === "object" && e !== null && "status" in e) {
          const errorStatus = (e as { status: string }).status;
          if (["quota_exceeded", "system_busy"].includes(errorStatus)) {
            if (errorStatus === "quota_exceeded") {
              captureEvent(EventName.mw_tts_limit_shown, {
                limit_type: "quota",
                ...eventProperties,
              });
              setShowLimitReachedOverlay(true);
            } else {
              captureEvent(errorEvent, {
                ...eventProperties,
                error_status: errorStatus,
              });
              toast.error(errorMap[errorStatus].title, { description: errorMap[errorStatus].description });
            }
          } else {
            captureEvent(errorEvent, {
              ...eventProperties,
              error_status: "unknown",
            });
            toast.error("Something went wrong", {
              description: "It looks like there was an issue while generating. Please try again later.",
            });
          }
        } else {
          captureEvent(errorEvent, {
            ...eventProperties,
            error_status: "unknown",
          });
          toast.error("Something went wrong", {
            description: "It looks like there was an issue while generating. Please try again later.",
          });
        }
        cleanupFunction?.();
        setTimeout(() => setPlayingState("idle"), 1000);
      }
    },
    [customTtsGenerator, selectedVoice, speed, text, useStreaming, selectedLanguage],
  );

  const handlePlay = useCallback(() => {
    if (!audioElementRef.current) return;

    switch (playingState) {
      case "idle":
        if (regenerate) {
          setActiveSampleId(null);
          generate({
            modelId: model,
            textPrompt: text,
            voice: selectedVoice,
            isDialogueMode: isDialogMode,
          });
        } else {
          audioElementRef.current.play();
        }
        break;
      case "playing":
        audioElementRef.current.pause();
        break;
      case "paused":
        if (regenerate) {
          setActiveSampleId(null);
          generate({
            modelId: model,
            textPrompt: text,
            voice: selectedVoice,
            isDialogueMode: isDialogMode,
          });
        } else {
          audioElementRef.current.play();
        }
        break;
    }
  }, [generate, playingState, regenerate, model, text, selectedVoice, isDialogMode]);

  const handleDemoPromptSelected = useCallback(
    (prompt: IUseCasePromptWithId, selectedVoices: Voice[]) => {
      const isDialogue = !!prompt.features?.[PromptFeature.DIALOGUE];
      captureEvent(EventName.mw_tts_sample_selected, {
        source_component: "tts_omnibox",
        current_language: selectedLanguage,
        model_id: model,
        prompt_id: prompt.id,
        is_dialogue: isDialogue,
      });

      if (isDialogue) {
        // Handle dialogue prompts with multiple speakers
        const samples = convertDialogueToSamples(prompt.dialogue || [], selectedVoices);
        setSelectedDialogue({
          samples,
          assignedVoices: selectedVoices,
        });
        generate({
          modelId: model,
          isDialogueMode: true,
          dialogueModeSamples: samples,
        });
      } else {
        // Handle single-speaker prompts
        setSelectedDialogue(null);
        setSelectedVoice(selectedVoices[0]);
        setText(prompt.text);
        generate({
          modelId: model,
          isDialogueMode: false,
          textPrompt: prompt.text,
          voice: selectedVoices[0],
        });
      }

      setActiveSampleId(prompt.id);
    },
    [generate, model],
  );

  const toggleExpanded = useCallback(() => {
    setIsExpanded(!isExpanded);
  }, [isExpanded]);

  const isPlayButtonDisabled =
    !text.trim() || playingState === "loading" || text.length > CHARACTER_LIMIT || !selectedVoice;

  return (
    <>
      <audio
        className='h-24 w-24 border'
        onEnded={() => setPlayingState("idle")}
        onPause={() => setPlayingState("paused")}
        onPlay={() => setPlayingState("playing")}
        ref={audioElementRef}
      />
      <Wrapper variant={variant} size={size}>
        <m.div
          animate={{
            position: animationState === "original" ? "relative" : "fixed",
            top: animationState === "full-screen" ? 0 : (elementRect.current?.top ?? "auto"),
            left: animationState === "full-screen" ? 0 : (elementRect.current?.left ?? "auto"),
            width: animationState === "full-screen" ? "100vw" : (elementRect.current?.width ?? "100%"),
            height: animationState === "full-screen" ? "100vh" : (elementRect.current?.height ?? "100%"),
            zIndex: animationState === "original" ? "auto" : 9999,
          }}
          className={clsx("flex", variant !== "simple" && "overflow-hidden bg-default")}
          initial={false}
          ref={containerRef}
          transition={{
            duration: animationState === "original" || animationState === "popping" ? 0 : 0.2,
            type: animationState === "popping-back" ? "tween" : "spring",
            stiffness: 400,
            damping: 25,
            restDelta: 0.001,
          }}
        >
          {showLimitReachedOverlay && (
            <LimitReachedOverlay
              trackingConfig={{
                signUpLink: {
                  eventName: EventName.mw_limit_reached_overlay_sign_up_clicked,
                  eventProperties: {
                    product: "tts",
                    limit_type: "quota",
                  },
                },
              }}
              onClose={() => setShowLimitReachedOverlay(false)}
            />
          )}
          {variant === "sidebar" && (
            <>
              <div className='flex w-full flex-col overflow-x-hidden'>
                <div className={cn("flex-grow md:hidden", hideSimpleView && "hidden")}>
                  <SimpleMobile
                    activeSampleId={activeSampleId ?? undefined}
                    handlePlayPause={handlePlay}
                    initialLanguage={initialLanguage}
                    initialPrompts={initialPrompts}
                    onSelected={handleDemoPromptSelected}
                    playingState={playingState}
                    samplePrompts={samplePrompts}
                    selectedLanguage={selectedLanguage}
                    useV3Prompts={model === TTSModels.ELEVEN_V3}
                    voices={voices}
                  />
                </div>
                <div className={cn("hidden md:contents", !hideSimpleView ? "hidden" : "contents")}>
                  <TextInput
                    alignment={alignment}
                    audioElementRef={audioElementRef}
                    handleTextChange={handleTextChange}
                    isDialogMode={isDialogMode}
                    markNeedsRegeneration={markNeedsRegeneration}
                    model={model}
                    multiSpeakerRef={multiSpeakerRef}
                    placeholder={placeholder}
                    playingState={playingState}
                    selectedDialogue={selectedDialogue}
                    text={text}
                    useStreaming={useStreaming}
                    voices={voices}
                  />
                  <div className='relative flex flex-col items-center'>
                    <MediaProgressBar className='h-1 w-full' mediaElement={audioElementRef.current || null} />
                    {!hideSamples && (
                      <VoicePickerTags
                        activeSampleId={activeSampleId ?? undefined}
                        handlePlayPause={handlePlay}
                        initialLanguage={initialLanguage}
                        initialPrompts={initialPrompts}
                        onSelected={handleDemoPromptSelected}
                        playingState={playingState}
                        samplePrompts={samplePrompts}
                        selectedLanguage={selectedLanguage}
                        singleLine
                        useV3Prompts={model === TTSModels.ELEVEN_V3}
                        voices={voices}
                      />
                    )}
                  </div>
                </div>
                <div className='order-2 flex items-center justify-between gap-12 p-16 pt-8 md:hidden md:pt-16'>
                  <LanguageSelect
                    language={language}
                    onLanguageChange={handleLanguageChange}
                    sortedLanguages={sortedLanguages}
                  />
                  {!hideSimpleView && <TTSHideSimpleViewButton setHideSimpleView={setHideSimpleView} />}
                  <PlayButton
                    className={cn(!hideSimpleView && "hidden md:flex")}
                    disabled={isPlayButtonDisabled}
                    onPlay={handlePlay}
                    playingState={playingState}
                  />
                </div>
              </div>
              {/* OPTIONAL SIDEBAR */}
              <div className='relative hidden w-2/5 flex-col gap-20 overflow-y-auto border-l border-solid p-24 md:flex'>
                <div className='flex items-center justify-between'>
                  <div className='flex items-center gap-8'>
                    <Settings2Icon size={16} />
                    <span className='f-ui-03'>{t("widget.voice_settings")}</span>
                  </div>
                  <Button className='h-40 w-40 p-0' onClick={toggleExpanded} size='sm' variant='elevated'>
                    {isExpanded ? <ShrinkIcon size={16} /> : <ExpandIcon size={16} />}
                  </Button>
                </div>
                {!isDialogMode && (
                  <VoiceSelector
                    loading={voicesLoading}
                    onVoiceChange={handleVoiceChange}
                    selectedVoice={selectedVoice}
                    showTags
                    size={size}
                    variant='sidebar'
                    voices={voices}
                  />
                )}
                <LanguageSelect
                  language={language}
                  onLanguageChange={handleLanguageChange}
                  sortedLanguages={sortedLanguages}
                  variant='sidebar'
                />
                {!isDialogMode && (
                  <>
                    <ModelSelector models={availableModels} onModelChange={handleModelChange} selectedModel={model} />
                    <SpeedSelector onSpeedChange={handleSpeedChange} value={speed} />
                  </>
                )}
                <div className='mt-auto flex gap-8'>
                  {model !== TTSModels.ELEVEN_V3 && (
                    <DownloadButton
                      audioElementRef={audioElementRef}
                      canDownload={!regenerate && !!text && (!useStreaming || !!completeAudioData)}
                      completeAudioData={completeAudioData}
                      variant='sidebar'
                    />
                  )}
                  <PlayButton
                    onPlay={handlePlay}
                    playingState={playingState}
                    variant='sidebar'
                    disabled={isPlayButtonDisabled}
                  />
                </div>
              </div>
            </>
          )}
          {variant === "default" && (
            <div className={cn("relative flex w-full flex-col bg-dark p-4")}>
              <div
                className={cn(
                  "relative z-1 flex flex-grow flex-col overflow-x-hidden rounded-inxl bg-default shadow-xs",
                )}
              >
                <div className={cn("flex-grow md:hidden", hideSimpleView && "hidden")}>
                  <SimpleMobile
                    activeSampleId={activeSampleId ?? undefined}
                    handlePlayPause={handlePlay}
                    initialLanguage={initialLanguage}
                    initialPrompts={initialPrompts}
                    onSelected={handleDemoPromptSelected}
                    playingState={playingState}
                    samplePrompts={samplePrompts}
                    selectedLanguage={selectedLanguage}
                    useV3Prompts={model === TTSModels.ELEVEN_V3}
                    voices={voices}
                  />
                </div>
                <div className={cn("hidden md:contents", !hideSimpleView ? "hidden" : "contents")}>
                  <TextInput
                    alignment={alignment}
                    audioElementRef={audioElementRef}
                    handleTextChange={handleTextChange}
                    isDialogMode={isDialogMode}
                    markNeedsRegeneration={markNeedsRegeneration}
                    model={model}
                    multiSpeakerRef={multiSpeakerRef}
                    placeholder={placeholder}
                    playingState={playingState}
                    selectedDialogue={selectedDialogue}
                    text={text}
                    useStreaming={useStreaming}
                    voices={voices}
                  />
                  {!hideSamples && (
                    <VoicePickerTags
                      className='md:px-16 md:pb-8 lg:px-24'
                      handlePlayPause={handlePlay}
                      initialLanguage={initialLanguage}
                      initialPrompts={initialPrompts}
                      onSelected={handleDemoPromptSelected}
                      playingState={playingState}
                      samplePrompts={samplePrompts}
                      selectedLanguage={selectedLanguage}
                      useV3Prompts={model === TTSModels.ELEVEN_V3}
                      voices={voices}
                    />
                  )}
                </div>
                <MediaProgressBar
                  className='order-1 mx-0 h-1 lg:order-none'
                  mediaElement={audioElementRef.current || null}
                />
                <div className='relative hidden items-center px-16 lg:flex lg:px-24'>
                  <div className='py-16'>
                    <LanguageSelect
                      language={language}
                      onLanguageChange={handleLanguageChange}
                      showSelectedLabel
                      sortedLanguages={sortedLanguages}
                    />
                  </div>
                  <div className='ml-auto flex items-center'>
                    <PlayButton
                      disabled={isPlayButtonDisabled}
                      onPlay={handlePlay}
                      playingState={playingState}
                      showLabel
                    />
                    <DownloadButton
                      audioElementRef={audioElementRef}
                      canDownload={!regenerate && !!text && (!useStreaming || !!completeAudioData)}
                      completeAudioData={completeAudioData}
                    />
                  </div>
                </div>
                <div className='order-2 flex flex-wrap items-center gap-12 p-16 lg:hidden'>
                  <LanguageSelect
                    language={language}
                    onLanguageChange={handleLanguageChange}
                    showSelectedLabel={false}
                    sortedLanguages={sortedLanguages}
                  />
                  <div className='ml-auto flex items-center gap-8'>
                    {!hideSimpleView && <TTSHideSimpleViewButton setHideSimpleView={setHideSimpleView} />}

                    <PlayButton
                      className={cn("ml-0", !hideSimpleView && "hidden md:flex")}
                      disabled={isPlayButtonDisabled}
                      onPlay={handlePlay}
                      playingState={playingState}
                    />
                  </div>
                </div>
              </div>
              {model === TTSModels.ELEVEN_V3 && (
                <>
                  <div className='z-1 px-8 pb-12 pt-16 text-center'>
                    <p className='f-ui-03 normal-case'>{t("widget.footer")}</p>
                  </div>
                  <div
                    className='absolute bottom-0 right-0 z-0 h-[204px] w-[432px] max-w-[60%] opacity-90'
                    style={{
                      background:
                        "linear-gradient(0deg, rgba(243, 244, 246, 0.00) 0%, rgba(243, 244, 246, 0.04) 62.8%, #F3F4F6 100%), linear-gradient(270deg, rgba(243, 244, 246, 0.00) 32.65%, #F3F4F6 93.63%), linear-gradient(230deg, #FD7336 24.11%, #D7A7FF 42.89%, #AFFAFF 62.49%)",
                    }}
                  />
                </>
              )}
            </div>
          )}
          {variant === "simple" && (
            <OmniboxSimpleWrapper className='h-[19.25rem]'>
              <div className='flex h-full flex-col'>
                <div className='flex-1 overflow-y-auto'>
                  <TextInput
                    alignment={alignment}
                    audioElementRef={audioElementRef}
                    handleTextChange={handleTextChange}
                    isDialogMode={isDialogMode}
                    markNeedsRegeneration={markNeedsRegeneration}
                    model={model}
                    multiSpeakerRef={multiSpeakerRef}
                    placeholder={placeholder}
                    playingState={playingState}
                    selectedDialogue={selectedDialogue}
                    text={text}
                    useStreaming={useStreaming}
                    voices={voices}
                  />
                </div>
                <div className='relative flex items-center px-16 lg:px-24'>
                  <div className='py-16'>
                    <LanguageSelect
                      language={language}
                      onLanguageChange={handleLanguageChange}
                      showDropdownArrow={false}
                      showSelectedLabel
                      sortedLanguages={sortedLanguages}
                      triggerSize='md'
                    />
                  </div>
                  <div className='ml-auto flex items-center'>
                    <DownloadButton
                      audioElementRef={audioElementRef}
                      canDownload={!regenerate && !!text && (!useStreaming || !!completeAudioData)}
                      className='hidden lg:flex'
                      completeAudioData={completeAudioData}
                    />
                    <PlayButton
                      disabled={isPlayButtonDisabled}
                      onPlay={handlePlay}
                      playingState={playingState}
                      showLabel
                      variant='simple'
                      willRegenerate={regenerate}
                    />
                  </div>
                </div>
              </div>
            </OmniboxSimpleWrapper>
          )}
        </m.div>
      </Wrapper>
      {variant === "simple" && (
        <VoicePickerTags
          className='mt-8'
          handlePlayPause={handlePlay}
          initialLanguage={initialLanguage}
          initialPrompts={initialPrompts}
          onSelected={handleDemoPromptSelected}
          playingState={playingState}
          samplePrompts={samplePrompts}
          selectedLanguage={selectedLanguage}
          useV3Prompts={model === TTSModels.ELEVEN_V3}
          variant='sample-picker'
          voices={voices}
        />
      )}
    </>
  );
}

function TTSHideSimpleViewButton({ setHideSimpleView }: { setHideSimpleView: (hide: boolean) => void }) {
  const [isLoading, setIsLoading] = useState(false);
  const t = useTranslations("ClientKeys.TTS");

  const handleClick = useCallback(() => {
    setIsLoading(true);
    runAfterNextBrowserPaint(() => {
      setHideSimpleView(true);
    });
  }, [setHideSimpleView]);

  return (
    <Button className='md:hidden' onClick={handleClick} variant='secondary'>
      {isLoading ? <LoaderCircleIcon className='animate-spin' size={14} /> : t("widget.edit_text")}
    </Button>
  );
}
